import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { goodsDocumentsApi, type GoodsDocumentOption } from "../../api/documentTemplates";
import type { ApiError } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../components/Toast";

/**
 * "Dokumentacija uz robu" on the customer card: which of the gallery's documents that go with the goods this customer
 * wants with its goods. A dispatch order makes them all, with the lots' documents, once quality releases it. Only
 * documents in use are offered; the set is saved in the gallery's order.
 */
export function GoodsDocumentSetCard({ companyId, canEdit }: { companyId: string; canEdit: boolean }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const [options, setOptions] = useState<GoodsDocumentOption[] | null>(null);
  // The ticks while the set is being changed; null when it is not.
  const [picked, setPicked] = useState<Set<string> | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    setOptions(null);
    setPicked(null);
    goodsDocumentsApi.forCompany(companyId)
      .then((o) => { if (live) setOptions(o); })
      .catch(() => { if (live) setOptions([]); });
    return () => { live = false; };
  }, [companyId]);

  function toggle(id: string, on: boolean) {
    setPicked((p) => {
      const next = new Set(p);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  }

  async function save() {
    if (!picked || !options) return;
    setBusy(true);
    try {
      setOptions(await goodsDocumentsApi.setForCompany(companyId, options.filter((o) => picked.has(o.id)).map((o) => o.id)));
      setPicked(null);
      toast.success(t("documentTemplates.goods.saved", "The customer's set is saved."));
    } catch (e) {
      toast.error((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  const chosen = (options ?? []).filter((o) => o.chosen);

  return (
    <section className="card">
      <header className="card-h" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <div>
          <h3>{t("documentTemplates.goods.title", "Documents that go with the goods")}</h3>
          <div className="sub">
            {t("documentTemplates.goods.hint", "Made on the dispatch order once quality releases it, together with the lots' documents.")}
          </div>
        </div>
        {canEdit && picked === null && (options?.length ?? 0) > 0 && (
          <button type="button" className="btn sm" onClick={() => setPicked(new Set(chosen.map((o) => o.id)))}>
            {t("documentTemplates.goods.edit", "Change the set")}
          </button>
        )}
      </header>
      <div className="card-b">
        {options === null ? null : options.length === 0 ? (
          <div className="empty">
            {t("documentTemplates.goods.noTemplates", "The gallery has no documents that go with the goods yet.")}
            {hasPermission("section:document_templates:read") && (
              <>
                {" "}
                <Link to="/admin/document-templates">{t("documentTemplates.goods.toGallery", "Open the gallery")}</Link>
              </>
            )}
          </div>
        ) : picked === null ? (
          chosen.length === 0 ? (
            <div className="empty">{t("documentTemplates.goods.none", "No documents picked for this customer yet.")}</div>
          ) : (
            <ul style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 6, fontSize: 14 }}>
              {chosen.map((o) => (
                <li key={o.id}>
                  {o.name}
                  {o.description ? <span style={{ color: "var(--ink-3)" }}> — {o.description}</span> : null}
                </li>
              ))}
            </ul>
          )
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {options.map((o) => (
              <label key={o.id} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 14, color: "var(--ink-2)" }}>
                <input type="checkbox" checked={picked.has(o.id)} onChange={(e) => toggle(o.id, e.target.checked)} />
                <span>
                  {o.name}
                  {o.description ? <span style={{ display: "block", fontSize: 12, color: "var(--ink-3)" }}>{o.description}</span> : null}
                </span>
              </label>
            ))}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4 }}>
              <button type="button" className="btn" onClick={() => setPicked(null)} disabled={busy}>
                {t("documentTemplates.form.cancel", "Cancel")}
              </button>
              <button type="button" className="btn primary" onClick={() => void save()} disabled={busy}>
                {t("documentTemplates.form.save", "Save")}
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
